/**
 * DEV-1482 — per-user cache of the unlocked-feature set, used to seed the
 * LeftNav's FIRST paint so it never flashes tiers the user hasn't earned.
 *
 * THE BUG THIS EXISTS FOR. LeftNav gated on
 *   `!it.feature || !unlockedFeatures || unlockedFeatures.has(it.feature)`
 * — so while the onboarding fetch was in flight (`unlockedFeatures` undefined)
 * EVERY item rendered, and the fetched set then hid the higher tiers. An Explorer
 * logging in saw Champion and Govern flash past. That is not only jank: it is a
 * progressive-disclosure spoiler, showing a user the tiers the reveal discipline
 * deliberately withholds.
 *
 * THE NAMESPACING IS NOT OPTIONAL. The cache key carries the user GUID. An
 * unkeyed cache on a shared browser would seed the NEXT user's first paint from
 * the PREVIOUS user's tiers — a Governor logs out, an Explorer logs in, and the
 * Explorer flashes the Governor's nav. That is exactly the DEV-1443 bug class,
 * reborn in a different cache.
 *
 * Note the namespacing is what protects this, NOT DEV-1443's identity flush —
 * that flush clears the query cache and the in-memory stores, not localStorage.
 * Keying by GUID means user B simply reads a different key; user A's seed sits
 * there harmlessly and is correct again when A returns.
 *
 * PIXELS, NEVER PERMISSIONS. This seeds what the nav DRAWS, and nothing else.
 * Every gated action still checks the live, server-derived set. A stale cache
 * showing a tier the server has since revoked corrects itself on fetch — and a
 * nav item that briefly renders is not access. Fail-closed on the way in
 * (no cache → nothing gated shows); server-authoritative on the way out.
 */

import type { FeatureFlag } from './archetypeConfig';

const KEY_PREFIX = 'l4e_nav_features:';

/** Bump to invalidate every cached seed (e.g. if FeatureFlag values change). */
const VERSION = 'v1';

function keyFor(userGuid: string): string {
  return `${KEY_PREFIX}${VERSION}:${(userGuid || '').toUpperCase()}`;
}

/**
 * The last-known feature set for this user, or NULL when we've never seen them
 * on this device.
 *
 * NULL is meaningfully different from an empty set: null means "unknown, fail
 * closed to the baseline", empty means "we know they have nothing unlocked".
 * Both draw the same nav today, but conflating them would make the cache
 * impossible to reason about later.
 */
export function readCachedNavFeatures(userGuid: string): Set<FeatureFlag> | null {
  if (!userGuid) return null;
  try {
    const raw = window?.localStorage?.getItem(keyFor(userGuid));
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return null;
    return new Set(arr as FeatureFlag[]);
  } catch {
    return null;   // corrupt or unavailable → fail closed
  }
}

export function writeCachedNavFeatures(userGuid: string, features: Set<FeatureFlag>): void {
  if (!userGuid) return;
  try {
    window?.localStorage?.setItem(keyFor(userGuid), JSON.stringify([...features]));
  } catch {
    /* storage full or blocked — the nav still works, it just won't pre-seed */
  }
}

/**
 * What the nav may draw before we know anything about this user.
 *
 * EMPTY — deliberately. A first-time visitor on a fresh device IS an Explorer,
 * and showing them only the ungated items is correct by construction. The cost is
 * that an Explorer's own gated items pop in a beat later on their very first
 * paint on a new device; the alternative (guessing a baseline) risks showing
 * something unearned, which is the bug. Pop-in over spoiler.
 */
export const NAV_FEATURES_FAIL_CLOSED: Set<FeatureFlag> = new Set<FeatureFlag>();
